import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MarmorariaAgendamento, MarmorariaService } from '../../service/marmoraria.service';
import { RomanieioPdfComponent } from './romaneio-pdf.component/romaneio-pdf.component';
import { RomanieioDaterRangeModalComponent } from './romaneio-date-range-modal/romaneio-date-range-modal';

@Component({
  selector: 'app-agendamento.component',
  standalone: true,
  imports: [CommonModule, FormsModule, RomanieioPdfComponent, RomanieioDaterRangeModalComponent],
  templateUrl: './agendamento.component.html',
  styleUrls: ['./agendamento.component.css']
})
export class AgendamentoComponent implements OnInit {
  @ViewChild(RomanieioDaterRangeModalComponent) dateRangeModal!: RomanieioDaterRangeModalComponent;
  @ViewChild(RomanieioPdfComponent) romanieioPdf!: RomanieioPdfComponent;
  
  agendamentos: MarmorariaAgendamento[] = [];
  filteredAgendamentos: MarmorariaAgendamento[] = [];
  pagedAgendamentos: MarmorariaAgendamento[] = [];
  searchTerm = '';
  isLoadingRomaneio = false;

  currentPage = 0;
  itemsPerPage = 10;
  totalPages = 1;
  totalElements = 0;
  maxPagesToShow = 5;
  startPage = 0;
  endPage = 0;
  pages: number[] = [];
  deletingId: number | null = null;

  // Calendar properties
  currentMonth = new Date().getMonth();
  currentYear = new Date().getFullYear();
  calendarDays: (number | null)[] = [];
  selectedDate: Date | null = null;
  monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
  agendamentosPorDia: Map<string, number> = new Map();

  constructor(
    private marmorariaService: MarmorariaService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadAgendamentos();
    this.generateCalendar();
  }

  loadAgendamentos(): void {
    this.marmorariaService.getAgendamentos().subscribe({
      next: (data) => {
        this.agendamentos = data;
        this.filteredAgendamentos = [...data];
        this.countAgendamentosPorDia();
        this.currentPage = 0;
        this.refreshPagination();
      },
      error: () => {
        this.agendamentos = [];
        this.filteredAgendamentos = [];
        this.pagedAgendamentos = [];
        this.totalElements = 0;
        this.totalPages = 1;
        this.pages = [];
        this.agendamentosPorDia.clear();
      }
    });
  }

  countAgendamentosPorDia(): void {
    this.agendamentosPorDia.clear();
    this.agendamentos.forEach((agendamento) => {
      const data = new Date(agendamento.dataAgendamento);
      const key = this.getDateKey(data);
      this.agendamentosPorDia.set(key, (this.agendamentosPorDia.get(key) || 0) + 1);
    });
  }

  generateCalendar(): void {
    const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
    const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
    this.calendarDays = [];

    // Adiciona cells vazias para dias do mês anterior
    for (let i = 0; i < firstDay; i++) {
      this.calendarDays.push(null);
    }

    // Adiciona dias do mês
    for (let day = 1; day <= daysInMonth; day++) {
      this.calendarDays.push(day);
    }
  }

  previousMonth(): void {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateCalendar();
  }

  selectDay(day: number | null): void {
    if (day === null) return;

    this.selectedDate = new Date(this.currentYear, this.currentMonth, day);
    this.filterAgendamentosByDate();
  }

  filterAgendamentosByDate(): void {
    if (!this.selectedDate) {
      this.filteredAgendamentos = [...this.agendamentos];
    } else {
      const selectedDateKey = this.getDateKey(this.selectedDate);
      this.filteredAgendamentos = this.agendamentos.filter((agendamento) => {
        const agendamentoDate = new Date(agendamento.dataAgendamento);
        return this.getDateKey(agendamentoDate) === selectedDateKey;
      });
    }
    this.currentPage = 0;
    this.refreshPagination();
  }

  clearDateFilter(): void {
    this.selectedDate = null;
    this.filteredAgendamentos = [...this.agendamentos];
    this.currentPage = 0;
    this.refreshPagination();
  }

  private getDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getAgendamentosCountForDay(day: number | null): number {
    if (day === null) return 0;
    const dateKey = this.getDateKey(new Date(this.currentYear, this.currentMonth, day));
    return this.agendamentosPorDia.get(dateKey) || 0;
  }

  isSelectedDay(day: number | null): boolean {
    if (!day || !this.selectedDate) return false;
    return (
      this.selectedDate.getFullYear() === this.currentYear &&
      this.selectedDate.getMonth() === this.currentMonth &&
      this.selectedDate.getDate() === day
    );
  }

  isToday(day: number | null): boolean {
    if (!day) return false;
    const today = new Date();
    return (
      today.getFullYear() === this.currentYear &&
      today.getMonth() === this.currentMonth &&
      today.getDate() === day
    );
  }

  search(): void {
    const term = this.searchTerm.trim().toLowerCase();

    if (!term) {
      this.filteredAgendamentos = [...this.agendamentos];
    } else {
      this.filteredAgendamentos = this.agendamentos.filter((agendamento) =>
        `${agendamento.tipoAgendamento} ${agendamento.enderecoAgendamento} ${agendamento.telefoneContato} ${agendamento.descricao} ${agendamento.complexidade} ${agendamento.cliente.nomeRazaoSocial}`
          .toLowerCase()
          .includes(term)
      );
    }

    this.currentPage = 0;
    this.refreshPagination();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filteredAgendamentos = [...this.agendamentos];
    this.currentPage = 0;
    this.refreshPagination();
  }

  novoAgendamento(): void {
    this.router.navigate(['/home', 'agendamento-manutencao']);
  }

  editarAgendamento(agendamento: MarmorariaAgendamento): void {
    if (!agendamento.id) {
      return;
    }

    this.router.navigate(['/home', 'agendamento-manutencao', agendamento.id]);
  }

  excluirAgendamento(agendamento: MarmorariaAgendamento): void {
    if (!agendamento.id || this.deletingId !== null) {
      return;
    }

    const confirmDelete = window.confirm('Deseja realmente excluir este agendamento?');
    if (!confirmDelete) {
      return;
    }

    this.deletingId = agendamento.id;
    this.marmorariaService.deleteAgendamento(agendamento.id).subscribe({
      next: () => {
        this.toastr.success('Agendamento excluído com sucesso.');
        this.loadAgendamentos();
      },
      error: () => {
        this.toastr.error('Não foi possível excluir o agendamento.');
      },
      complete: () => {
        this.deletingId = null;
      }
    });
  }

  setPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }

    this.currentPage = page;
    this.updatePagedData();
    this.updatePageNumbers();
  }

  private refreshPagination(): void {
    this.totalElements = this.filteredAgendamentos.length;
    this.totalPages = Math.max(1, Math.ceil(this.totalElements / this.itemsPerPage));
    this.updatePagedData();
    this.updatePageNumbers();
  }

  private updatePagedData(): void {
    const start = this.currentPage * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    this.pagedAgendamentos = this.filteredAgendamentos.slice(start, end);
  }

  private updatePageNumbers(): void {
    const halfPages = Math.floor(this.maxPagesToShow / 2);
    this.startPage = Math.max(0, this.currentPage - halfPages);
    this.endPage = Math.min(this.totalPages - 1, this.startPage + this.maxPagesToShow - 1);

    if (this.endPage - this.startPage + 1 < this.maxPagesToShow) {
      this.startPage = Math.max(0, this.endPage - this.maxPagesToShow + 1);
    }

    this.pages = Array.from(
      { length: this.endPage - this.startPage + 1 },
      (_, i) => this.startPage + i
    );
  }

  abrirModalRomaneio(): void {
    this.dateRangeModal.open();
  }

  gerarRomaneio(event: { inicio: string; fim: string }): void {
    this.isLoadingRomaneio = true;
    this.marmorariaService.getAgendamentosPorPeriodo(event.inicio, event.fim).subscribe({
      next: (agendamentos) => {
        if (agendamentos && agendamentos.length > 0) {
          this.romanieioPdf.printRomaneio(agendamentos, event.inicio, event.fim);
        } else {
          this.toastr.info('Não há agendamentos neste período.');
        }
      },
      error: () => {
        this.toastr.error('Erro ao buscar agendamentos. Por favor, tente novamente.');
      },
      complete: () => {
        this.isLoadingRomaneio = false;
      }
    });
  }

}
